import { MODEL_NAMES } from '../../constants/index';
import { cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import MessagingRoutesBaseHandler from './base-handler.js';

export default class MessagingRoutesConversationDetailsHandler extends MessagingRoutesBaseHandler {
  @service('spds/toast') toast;
  @service clientProfileNavigator;
  @service intl;
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service store;

  get conversation() {
    return this.options.parentHandler.conversation;
  }

  get isDraft() {
    return this.conversation.isDraft;
  }

  get showLeaveButton() {
    const { conversation } = this;

    return (
      this.messaging.groupMessagingEnabled &&
      this.messaging.currentProfile?.isClinician &&
      !conversation?.isDraft
    );
  }

  get isLeaveDisabled() {
    return this.showLeaveButton && this.members.filter(member => member.isClinician).length <= 1;
  }

  @cached
  get members() {
    return this.conversation.currentMembers(this.messaging.currentProfile?.user);
  }

  @cached
  get previousMembers() {
    return this.conversation.previousMembers(this.messaging.currentProfile?.user);
  }

  get hasPreviousMembers() {
    return this.conversation.hasPreviousMembers;
  }

  canNavigateToClientProfile(member) {
    if (!member.isClient) {
      return false;
    }

    return this.clientProfileNavigator.canNavigateToClientProfile(
      [member],
      this.messaging.currentProfile.isClinician
    );
  }

  navigateToClientProfile = task({ drop: true }, async member => {
    try {
      await this.clientProfileNavigator.navigateToClientProfile(this.conversation, [member]);
    } catch (error) {
      this.toast.error(this.intl.t('emc.general_error'));
    }
  });

  updateDisplayName = async displayName => {
    const { conversation } = this;
    conversation.displayName = displayName;

    try {
      await conversation.save();
    } catch (error) {
      conversation.rollbackAttributes();
      throw error;
    }
  };

  async leaveConversation() {
    const conversation = this.conversation;
    const conversationId = conversation.id;

    try {
      await this.store.adapterFor(MODEL_NAMES.CONVERSATION).leave(conversationId);
    } catch (error) {
      this.messagingLogging.error(error, {
        context: 'leave-conversation',
        conversationId,
      });
      this.toast.error(this.intl.t('emc.general_error'));
      return;
    }

    this.toast.success(this.intl.t('emc.conversation_details.leave_success'));
    this.messaging.conversations?.removeObject?.(conversation);
    this.messaging.currentProfile?.removeUnreadConversation(conversationId, { shouldTrack: false });
    this.messagingNavigation.invalidateHistory(conversationId);
    await this.messagingNavigation.toConversationList({ recordHistory: false });

    if (!conversation.isDestroyed) {
      conversation.unloadRecord?.();
    }
  }
}
